import {AxisRight} from '@visx/axis';
import {LinearGradient} from '@visx/gradient';
import {Group} from '@visx/group';
import {scaleBand} from '@visx/scale';
import {getStringWidth, Text} from '@visx/text';
import React from 'react';

import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';

import {useBenchlingChartContext} from './BenchlingChartContext';
import {GradientSeriesColorConfig} from './utils/getSeriesColorConfig';
import {DEFAULT_LABEL_STYLE} from './utils/Theme';

export const LEGEND_BAR_WIDTH = 25;
export const LEGEND_LABEL_PADDING = 25;

/**
 *  Returns the width at the widest part of GradientSeriesLegend including padding.
 */
export function estimateGradientSeriesLegendWidth(
  legendLabel: string,
  minMetricValue: number,
  maxMetricValue: number
): number {
  const labelWidth = getStringWidth(legendLabel, DEFAULT_LABEL_STYLE) as number;
  const minMetricValueWidth = getStringWidth(
    prettyChartNumber(minMetricValue),
    DEFAULT_LABEL_STYLE
  ) as number;
  const maxMetricValueWidth = getStringWidth(
    prettyChartNumber(maxMetricValue),
    DEFAULT_LABEL_STYLE
  ) as number;
  const legendWidthWithoutPadding = Math.max(
    labelWidth,
    minMetricValueWidth + LEGEND_BAR_WIDTH,
    maxMetricValueWidth + LEGEND_BAR_WIDTH
  );
  return legendWidthWithoutPadding + 2 * LEGEND_LABEL_PADDING;
}

export interface GradientSeriesLegendProps {
  chartHeight: number;
  chartWidth: number;
  colorConfig: GradientSeriesColorConfig;
  label: string;
}

export default function GradientSeriesLegend({
  chartHeight,
  chartWidth,
  colorConfig: {colorScale},
  label,
}: GradientSeriesLegendProps): JSX.Element {
  const barHeight = chartHeight * 0.25;
  const x = chartWidth + LEGEND_LABEL_PADDING;
  const y = 0.5 * (chartHeight - barHeight - LEGEND_LABEL_PADDING);

  const {clientId} = useBenchlingChartContext();

  const gradientId = `gradient-legend-${clientId}`;

  const legendScale = scaleBand({
    domain: colorScale.domain(),
    paddingInner: 0.9,
    range: [barHeight, 0],
  });

  return (
    <Group left={x} top={y}>
      <LinearGradient id={gradientId} from={colorScale.range()[1]} to={colorScale.range()[0]} />
      <Text style={DEFAULT_LABEL_STYLE}>{label}</Text>
      <rect
        fill={`url(#${gradientId})`}
        height={barHeight}
        width={LEGEND_BAR_WIDTH}
        y={LEGEND_LABEL_PADDING}
      />
      <AxisRight
        hideAxisLine={true}
        hideTicks={true}
        left={LEGEND_BAR_WIDTH}
        scale={legendScale}
        tickFormat={(value) => prettyChartNumber(value)}
        tickLabelProps={() => DEFAULT_LABEL_STYLE}
        top={LEGEND_LABEL_PADDING}
      />
    </Group>
  );
}
